import React from 'react';
import CollectionPage from '../shared/CollectionPage';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { Navigate } from 'react-router-dom';
import MusicPriceValue from './MusicPriceValue';

/**
 * Music Collection Dashboard component
 * Uses the generic CollectionPage with music-specific configuration
 */
const MusicDashboard = () => {
  const { isFeatureEnabled } = useFeatureFlags();
  
  // Check if the music feature is enabled
  if (!isFeatureEnabled('enableMusicFeature')) {
    return <Navigate to="/" replace />;
  }

  // Music-specific filter options
  const musicFilterOptions = [
    { value: '', label: 'All Formats' },
    { value: 'Vinyl', label: 'Vinyl' },
    { value: 'CD', label: 'CD' },
    { value: 'Cassette', label: 'Cassette' },
    { value: '8-Track', label: '8-Track' },
    { value: 'Digital', label: 'Digital' },
    { value: 'Box Set', label: 'Box Set' },
    { value: 'Other', label: 'Other' }
  ];

  // Music-specific render props
  const musicRenderProps = {
    getImageUrl: (item) => item.imageUrl,
    getItemName: (item) => item.name,
    getItemSubtitle: (item) => {
      const base = `Artist: ${item.artist || 'Unknown'} • Format: ${item.format || 'Not specified'}`;
      if (item.format === 'Vinyl') {
        const parts = [];
        if (item.rpm) parts.push(`${item.rpm} RPM`);
        if (item.pressing) parts.push(item.pressing);
        if (parts.length) return `${base} • ${parts.join(' • ')}`;
      }
      return base;
    },
    getItemDetails: (item) => `Year: ${item.year || 'N/A'} • Genre: ${item.genre || 'N/A'} • Label: ${item.label || 'N/A'}`,
    // Enable price lookup for music
    showMarketValue: () => true,
    renderPriceValue: (item) => <MusicPriceValue item={item} />
  };

  // Music-specific filter function
  const getMusicFilterValue = (item) => item.format;

  // Music-specific edit fields
  const musicEditFields = [
    {
      name: 'name',
      label: 'Album Title',
      type: 'text',
      required: true,
      gridProps: { xs: 12 }
    },
    {
      name: 'artist',
      label: 'Artist',
      type: 'text',
      required: true,
      gridProps: { xs: 12 }
    },
    {
      name: 'format',
      label: 'Format',
      type: 'select',
      options: [
        { value: 'Vinyl', label: 'Vinyl' },
        { value: 'CD', label: 'CD' },
        { value: 'Cassette', label: 'Cassette' },
        { value: '8-Track', label: '8-Track' },
        { value: 'Digital', label: 'Digital' },
        { value: 'Box Set', label: 'Box Set' },
        { value: 'Other', label: 'Other' }
      ],
      required: true,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'Mint (M)', label: 'Mint (M)' },
        { value: 'Near Mint (NM)', label: 'Near Mint (NM)' },
        { value: 'Very Good Plus (VG+)', label: 'Very Good Plus (VG+)' },
        { value: 'Very Good (VG)', label: 'Very Good (VG)' },
        { value: 'Good (G)', label: 'Good (G)' },
        { value: 'Poor (P)', label: 'Poor (P)' },
        { value: 'Factory Sealed', label: 'Factory Sealed' },
        { value: 'New', label: 'New' },
        { value: 'Like New', label: 'Like New' },
        { value: 'Good / Acceptable', label: 'Good / Acceptable' },
        { value: 'For Parts / Not Working', label: 'For Parts / Not Working' }
      ],
      required: true,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'edition',
      label: 'Edition',
      type: 'select',
      options: [
        { value: 'Standard Release', label: 'Standard Release' },
        { value: 'Limited Edition', label: 'Limited Edition' },
        { value: 'First Pressing', label: 'First Pressing' },
        { value: 'Reissue', label: 'Reissue' },
        { value: 'Remastered', label: 'Remastered' },
        { value: 'Deluxe Edition', label: 'Deluxe Edition' },
        { value: 'Collector\'s Edition', label: 'Collector\'s Edition' },
        { value: 'Anniversary Edition', label: 'Anniversary Edition' },
        { value: 'Promotional Copy', label: 'Promotional Copy' },
        { value: 'Import', label: 'Import' },
        { value: 'Colored Vinyl', label: 'Colored Vinyl' },
        { value: 'Picture Disc', label: 'Picture Disc' },
        { value: 'Box Set', label: 'Box Set' },
        { value: 'Not Specified', label: 'Not Specified' }
      ],
      required: true,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'rpm',
      label: 'RPM',
      type: 'select',
      options: [
        { value: '33⅓', label: '33⅓ RPM' },
        { value: '45',  label: '45 RPM' },
        { value: '78',  label: '78 RPM' },
      ],
      showWhen: (data) => data.format === 'Vinyl',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'pressing',
      label: 'Pressing',
      type: 'select',
      options: ['Original','Reissue','Remaster','Picture Disc','Coloured Vinyl','Test Pressing','Promo','Unknown'].map(v => ({ value: v, label: v })),
      showWhen: (data) => data.format === 'Vinyl',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'vinylColour',
      label: 'Vinyl Colour',
      type: 'text',
      showWhen: (data) => data.format === 'Vinyl',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'sleeveCondition',
      label: 'Sleeve Condition',
      type: 'select',
      options: ['Mint (M)','Near Mint (NM)','Very Good Plus (VG+)','Very Good (VG)','Good (G)','Poor (P)','Factory Sealed','New','Like New','Good / Acceptable','For Parts / Not Working'].map(v => ({ value: v, label: v })),
      showWhen: (data) => data.format === 'Vinyl',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'catalogueNumber',
      label: 'Catalogue Number',
      type: 'text',
      showWhen: (data) => data.format === 'Vinyl',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'country',
      label: 'Country of Pressing',
      type: 'text',
      showWhen: (data) => data.format === 'Vinyl',
      required: false,
      gridProps: { xs: 12, sm: 6 },
    },
    {
      name: 'year',
      label: 'Year',
      type: 'text',
      required: false,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'genre',
      label: 'Genre',
      type: 'text',
      required: false,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'label',
      label: 'Label',
      type: 'text',
      required: false,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'imageUrl',
      label: 'Image URL',
      type: 'text',
      required: false,
      gridProps: { xs: 12 }
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'select',
      options: [
        { value: 'Low', label: 'Low' },
        { value: 'Medium', label: 'Medium' },
        { value: 'High', label: 'High' }
      ],
      required: true,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'listened',
      label: 'Listened',
      type: 'checkbox',
      required: false,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'price',
      label: 'Price',
      type: 'text',
      required: false,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'tracks',
      label: 'Tracks',
      type: 'textarea',
      required: false,
      gridProps: { xs: 12 }
    },
    {
      name: 'rating',
      label: 'Rating',
      type: 'rating',
      required: false,
      gridProps: { xs: 12 }
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      required: false,
      gridProps: { xs: 12 }
    }
  ];

  // Define table columns for sorting functionality
  const tableColumns = {
    name: { label: 'Album', sortable: true },
    artist: { label: 'Artist', sortable: true },
    format: { label: 'Format', sortable: true },
    year: { label: 'Year', sortable: true },
    genre: { label: 'Genre', sortable: true },
    label: { label: 'Label', sortable: true },
    condition: { label: 'Condition', sortable: true },
    listened: { label: 'Listened', sortable: true },
    price: { label: 'Price', sortable: true, numeric: true }
  };

  return (
    <CollectionPage
      collectionType="music"
      title="Music Collection"
      renderProps={musicRenderProps}
      filterOptions={musicFilterOptions}
      getFilterValue={getMusicFilterValue}
      editFields={musicEditFields}
      tableColumns={tableColumns}
    />
  );
};

export default MusicDashboard;
